/**
 * Kiểu dữ liệu chứng từ của module `ocr` (`/api/v1/m/ocr/documents…`, đợt 3–4).
 *
 * Chép đúng hợp đồng của `modules/ocr/backend/src/document/`: tên trường giữ `snake_case`.
 * Khi module có OpenAPI và client sinh tự động thì xoá tệp này.
 */
import type { DocumentType, ExtractionMode, FieldDataType, OcrSchemaDetail } from '../cau-hinh-ocr/types';

export const DOC_STATUSES = ['DRAFT', 'PROCESSING', 'PROCESSED', 'CONFIRMED', 'TRANSFERRED', 'ERROR'] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];

/** Giai đoạn của lượt quét. `DONE`, `FAILED` là giai đoạn cuối, không hiện thành nhãn đang chạy. */
export const OCR_STAGES = ['QUEUED', 'READING', 'READ_DONE', 'MAPPING', 'RETRY_WAIT', 'DONE', 'FAILED'] as const;
export type OcrStage = (typeof OCR_STAGES)[number];
/** Năm giai đoạn còn đang chạy — lọc được ở danh sách và có thẻ số liệu riêng. */
export const LIVE_STAGES = ['QUEUED', 'READING', 'READ_DONE', 'MAPPING', 'RETRY_WAIT'] as const;
export type LiveStage = (typeof LIVE_STAGES)[number];

export const OCR_LANGUAGES = ['vi', 'en', 'vi+en'] as const;
export type OcrLanguage = (typeof OCR_LANGUAGES)[number];

/** Tiến độ của job trong hàng đợi (`OcrJobStatus` của `hang-doi.ts`). */
export interface OcrJobStatus {
  state: string;
  progress: { step: string; label: string; percent: number } | null;
  failed_reason: string | null;
  attempt: number;
  max_attempts: number;
  queue_position: number | null;
}

export interface DocExtraFile {
  index: number;
  file_name: string | null;
  mime_type: string | null;
}

/** Các cột của `ocr_document` mà danh sách và chi tiết cùng trả. */
export interface DocBase {
  id: string;
  schema_id: string;
  schema_code: string;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  extra_files: DocExtraFile[];
  invoice_number: string | null;
  issue_date: string | null;
  seller_tax_code: string | null;
  seller_name: string | null;
  total_amount: string | number | null;
  vat_amount: string | number | null;
  grand_total: string | number | null;
  status: DocStatus;
  ocr_confidence: number | null;
  ocr_language: string | null;
  ocr_engine_version: string | null;
  ocr_error: string | null;
  ocr_stage: OcrStage | null;
  ocr_stage_at: string | null;
  ocr_attempt: number | null;
  ocr_error_step: 'READ' | 'MAP' | null;
  ocr_queued_at: string | null;
  ocr_started_at: string | null;
  ocr_read_at: string | null;
  ocr_finished_at: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  processed_by: string | null;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  has_transcript: boolean;
}

/** Một dòng của `GET /documents`. */
export interface DocListItem extends DocBase {
  schema: {
    name: string;
    type: DocumentType;
    ocr_provider: string | null;
    ocr_reader_provider: string | null;
    extraction_mode: ExtractionMode;
  };
  /** Chỉ có khi gọi với `include_live=true`. */
  ocr_live?: OcrJobStatus | null;
}

export interface DocPage {
  items: DocListItem[];
  next_cursor: string | null;
}

/** `GET /documents/stats`. */
export interface DocStats {
  total: number;
  draft: number;
  processing: number;
  confirmed: number;
  processed: number;
  transferred: number;
  error: number;
  processing_by_stage: Record<LiveStage | 'UNKNOWN', number>;
}

export interface DocValue {
  id: string;
  field_id: string;
  string_value: string | null;
  page_number: number | null;
  source_text: string | null;
  confidence: number | null;
  is_manually_edited: boolean;
  field: { field_key: string; label: string; data_type: FieldDataType; display_order: number };
}

export interface DocLineItem {
  id: string;
  stt: number;
  /** `null` = bảng đầu tiên của loại chứng từ (dữ liệu cũ một bảng). */
  table_key: string | null;
  name: string | null;
  unit: string | null;
  quantity: string | number | null;
  unit_price: string | number | null;
  amount: string | number | null;
  /** Giá trị các cột khác cột chuẩn, khoá theo `column_key`. */
  extra_data: Record<string, unknown> | null;
  is_manually_added: boolean;
}

/** `GET /documents/{id}`. */
export interface DocDetail extends DocBase {
  schema: OcrSchemaDetail;
  values: DocValue[];
  line_items: DocLineItem[];
}

/** `GET /documents/{id}/job-status`. */
export interface DocJobStatus {
  document_id: string;
  status: DocStatus;
  ocr_stage: OcrStage | null;
  job: OcrJobStatus;
}

/** `POST /documents/upload` (`202`). */
export interface DocUploadAccepted {
  document_id: string;
  job_id: string | null;
  status: 'QUEUED';
  message: string;
}

export interface DocUploadInput {
  files: File[];
  schema_id: string;
  language?: OcrLanguage;
  ocr_provider?: string;
  ocr_reader_provider?: string;
}

/** Thân `POST /documents/{id}/reprocess`. */
export interface DocReprocessInput {
  reuse_transcript?: boolean;
  from_failed_step?: boolean;
  ocr_provider?: string;
}

/** Thân `PATCH /documents/{id}`: chỉ gửi giá trị trường đã sửa. */
export interface DocPatch {
  values?: Array<{ field_id: string; string_value: string | null }>;
  status?: 'DRAFT';
}

/** `POST /documents/bulk-*`. */
export interface BulkResult {
  confirmed?: number;
  transferred?: number;
  deleted?: number;
  skipped: string[];
}

/** Một bản chép của chứng từ hay của kho bản OCR (`GET …/transcript`). */
export interface TranscriptFile {
  file_index: number;
  file_name: string | null;
  mime_type: string | null;
  reader_label: string | null;
  is_text_layer: boolean;
  transcribed_at: string | null;
  ocr_transcript_id: string | null;
  reused: boolean;
  pages: Array<{ page_number: number; content: string }>;
}

export interface DocTranscript {
  document_id?: string;
  source_file_id?: string;
  page_count: number;
  files: TranscriptFile[];
}

/**
 * Tệp gốc để xem trước: kho `files/sign` trả liên kết ký ngắn hạn, bản dev trả thẳng
 * nội dung tệp (ADR-0070 mục 1). Repository gói hai dạng về một kiểu.
 */
export type TepXemTruoc =
  | { kieu: 'lien-ket'; url: string; file_name: string; mime_type: string }
  | { kieu: 'noi-dung'; blob: Blob; file_name: string; mime_type: string };
